import { getUpdateClientUrl } from 'generatedTypes/client/services/client-controller';
import { useState } from 'react';
import { isDemoHiddenName } from 'utils/demoRecordFilter';
import { normalizePayload } from 'utils/normalizePayload';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { CLIENTS_API } from './constants';
import type { ClientResponse, CreateClient, FetchClient, UpdateClient } from './types';

const useClient = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchClient: FetchClient = async ({ id }) => {
        try {
            setIsLoading(true);

            const response = await withRetry(
                () => authenticatedGet<ClientResponse>(`${CLIENTS_API}/${id}`),
                CONNECTION_ERROR,
                () => fetchClient({ id })
            );

            if (response && isDemoHiddenName(response.name)) {
                return null;
            }

            return response;
        } finally {
            setIsLoading(false);
        }
    };

    const createClient: CreateClient = async ({ client }) => {
        try {
            setIsLoading(true);

            return await withRetry(
                () =>
                    authenticatedPost<ClientResponse>(
                        CLIENTS_API,
                        JSON.stringify(normalizePayload({ ...client }))
                    ),
                SAVE_FAILED,
                () => createClient({ client })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateClient: UpdateClient = async ({ client, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateClientUrl(id);

            return await withRetry(
                () =>
                    authenticatedPut<ClientResponse>(
                        url,
                        JSON.stringify(normalizePayload({ ...client }))
                    ),
                SAVE_FAILED,
                () => updateClient({ client, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createClient,
        fetchClient,
        isLoading,
        updateClient
    };
};

export default useClient;
